import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '@/components/ui/dialog';
import { 
  Navigation, 
  Plus, 
  Trash2, 
  Home, 
  Building2, 
  TreePine,
  DoorOpen,
  Edit3,
  MapPin
} from 'lucide-react';
import { cn } from '@/lib/utils';

// Predefined icons for quick nav buttons
const NAV_ICONS = {
  home: Home,
  building: Building2,
  outdoor: TreePine,
  door: DoorOpen,
  default: MapPin,
};

// Default quick navigation points - empty by default, users create their own
const DEFAULT_NAV_POINTS = [];

export function QuickNavigation({ 
  quickNavPoints = [], 
  sweeps = [],
  onNavigate,
  onAddNavPoint,
  onRemoveNavPoint,
  sdkConnected = false
}) {
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [newNavPoint, setNewNavPoint] = useState({
    label: '',
    sweepId: '',
    icon: 'default',
    description: '',
  });

  // Combine default and custom nav points
  const allNavPoints = [...DEFAULT_NAV_POINTS, ...quickNavPoints];

  const handleNavigate = (navPoint) => {
    if (navPoint.sweepId && onNavigate) {
      onNavigate(navPoint.sweepId);
    } else if (sweeps.length > 0 && onNavigate) {
      // If no specific sweep configured, navigate to first available sweep
      onNavigate(sweeps[0].id);
    }
  };

  const handleAddNavPoint = () => {
    if (newNavPoint.label && onAddNavPoint) {
      onAddNavPoint({
        label: newNavPoint.label,
        sweepId: newNavPoint.sweepId || null,
        icon: newNavPoint.icon,
        description: newNavPoint.description,
        color: 'secondary',
      });
      setNewNavPoint({ label: '', sweepId: '', icon: 'default', description: '' });
      setIsAddDialogOpen(false);
    }
  };

  const IconComponent = (iconName) => {
    const Icon = NAV_ICONS[iconName] || NAV_ICONS.default;
    return Icon;
  };

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Navigation className="h-4 w-4 text-primary" />
          <span className="text-sm font-medium">Quick Navigation</span>
        </div>
        
        <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
          <DialogTrigger asChild>
            <Button variant="ghost" size="icon-sm" className="h-7 w-7">
              <Plus className="h-3.5 w-3.5" />
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Add Navigation Point</DialogTitle>
              <DialogDescription>
                Create a quick navigation button to jump to a specific location.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="nav-label">Button Label</Label>
                <Input
                  id="nav-label"
                  placeholder="e.g., Kitchen, Lobby, Office..."
                  value={newNavPoint.label}
                  onChange={(e) => setNewNavPoint(prev => ({ ...prev, label: e.target.value }))}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="nav-sweep">Sweep ID (optional)</Label>
                <Input
                  id="nav-sweep"
                  placeholder="Leave empty for auto-select"
                  value={newNavPoint.sweepId}
                  onChange={(e) => setNewNavPoint(prev => ({ ...prev, sweepId: e.target.value }))}
                />
                <p className="text-xs text-muted-foreground">
                  Find sweep IDs by navigating in the viewer and checking the console.
                </p>
              </div>

              <div className="space-y-2">
                <Label>Icon</Label>
                <div className="flex gap-2">
                  {Object.entries(NAV_ICONS).map(([key, Icon]) => (
                    <Button
                      key={key}
                      variant={newNavPoint.icon === key ? 'default' : 'outline'}
                      size="icon"
                      onClick={() => setNewNavPoint(prev => ({ ...prev, icon: key }))}
                    >
                      <Icon className="h-4 w-4" />
                    </Button>
                  ))}
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="nav-desc">Description (optional)</Label>
                <Input
                  id="nav-desc"
                  placeholder="Brief description..."
                  value={newNavPoint.description}
                  onChange={(e) => setNewNavPoint(prev => ({ ...prev, description: e.target.value }))}
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsAddDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleAddNavPoint} disabled={!newNavPoint.label}>
                Add Navigation
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Navigation Buttons Grid */}
      <div className="grid grid-cols-2 gap-2">
        {allNavPoints.map((navPoint) => {
          const Icon = IconComponent(navPoint.icon);
          const isCustom = !navPoint.id.startsWith('nav-inside') && !navPoint.id.startsWith('nav-outside');
          
          return (
            <div key={navPoint.id} className="relative group">
              <Button
                variant={navPoint.color === 'primary' ? 'default' : 'secondary'}
                className={cn(
                  "w-full h-auto py-3 flex flex-col items-center gap-1",
                  navPoint.color === 'primary' && "bg-primary hover:bg-primary/90"
                )}
                onClick={() => handleNavigate(navPoint)}
              >
                <Icon className="h-5 w-5" />
                <span className="text-xs font-medium">{navPoint.label}</span>
              </Button>
              
              {/* Delete button for custom nav points */}
              {isCustom && onRemoveNavPoint && (
                <Button
                  variant="destructive"
                  size="icon"
                  className="absolute -top-2 -right-2 h-5 w-5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemoveNavPoint(navPoint.id);
                  }}
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              )}
            </div>
          );
        })}
      </div>

      {/* SDK Status */}
      {!sdkConnected && (
        <p className="text-xs text-muted-foreground text-center">
          Navigation uses URL deep-linking (SDK limited)
        </p>
      )}
    </div>
  );
}

export default QuickNavigation;
